import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Key, ArrowRight, EnvelopeSimple, UsersThree, Star, CircleNotch, ShieldCheck } from "@phosphor-icons/react";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { setLicenseEmail, setInviteCode } from "@/lib/history";
import { getRefEmail } from "@/lib/track";
import { AgreeNote } from "@/components/FooterLinks";
import { toast } from "sonner";

// Inline pricing tiers with USD + HKD amounts and lookup keys.
// "single" is the low-friction Single Audit Pass surfaced after the free audit is used.
const TIERS = [
  { id: "single", name: "Single Audit Pass", cadence: "one-time", blurb: "One-time check · no subscription",
    usd: { amount: "$15", lookup_key: "lc_single_audit_usd" }, hkd: { amount: "HK$99", lookup_key: "lc_single_audit" } },
  { id: "professional", name: "Professional", cadence: "/mo", blurb: "Managed AI · 30 audits/mo · no key needed",
    usd: { amount: "$199", lookup_key: "lc_pro_monthly_usd" }, hkd: { amount: "HK$1,550", lookup_key: "lc_pro_monthly" } },
  { id: "firm", name: "Firm / Agency", cadence: "/mo", blurb: "100 audits/mo · up to 8 team seats",
    usd: { amount: "$599", lookup_key: "lc_firm_monthly_usd" }, hkd: { amount: "HK$4,680", lookup_key: "lc_firm_monthly" } },
  { id: "solo", name: "Solo Lifetime", cadence: "one-time", blurb: "Unlimited · bring your own API key",
    usd: { amount: "$499", lookup_key: "lc_solo_lifetime_usd" }, hkd: { amount: "HK$3,880", lookup_key: "lc_solo_lifetime" } },
];

export function PaywallModal({ open, onClose, onSwitchByo, onUnlocked, freeUsed = false, highlightSolo = false }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(false);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  // Paywall settles only in USD or HKD. EUR/GBP (reference display) route to USD.
  const [currency, setCurrency] = useState(() => (localStorage.getItem("lc_ccy") === "hkd" ? "hkd" : "usd"));
  const changeCurrency = (c) => { setCurrency(c); localStorage.setItem("lc_ccy", c); };

  const checkout = async (tier) => {
    setLoadingPlan(tier.id);
    try {
      const ref_email = getRefEmail();
      const r = await api.post("/payments/checkout", { lookup_key: tier[currency].lookup_key, origin_url: window.location.origin, ref_email });
      window.location.href = r.data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoadingPlan(null);
    }
  };

  const unlock = async () => {
    if (!email.trim()) { toast.error("Enter your checkout email."); return; }
    setChecking(true);
    try {
      const r = await api.post("/entitlement", { email: email.trim() });
      if (r.data.entitled) {
        setLicenseEmail(email.trim().toLowerCase());
        toast.success("Unlocked — welcome back.");
        onUnlocked?.();
        onClose?.();
      } else {
        toast.error("No active purchase found for that email.");
      }
    } catch {
      toast.error("Could not verify. Try again.");
    } finally {
      setChecking(false);
    }
  };

  const joinTeam = async () => {
    if (!code.trim() || !email.trim()) { toast.error("Enter your email and the invite code."); return; }
    setJoining(true);
    try {
      const r = await api.post("/team/join", { invite_code: code.trim(), email: email.trim() });
      if (r.data.entitled) {
        setInviteCode(code.trim().toUpperCase());
        setLicenseEmail(email.trim().toLowerCase());
        toast.success("Joined team — full access unlocked.");
        onUnlocked?.();
        onClose?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid invite code.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="paywall-modal"
        >
          <div className="absolute inset-0 bg-lc-navy/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="lc-scroll relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-sm border border-lc-ink/10 bg-white p-7 shadow-2xl"
          >
            <button onClick={onClose} data-testid="paywall-close"
              className="absolute right-4 top-4 text-lc-steel hover:text-lc-ink transition-colors duration-200">
              <X size={18} />
            </button>
            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-lc-navy">
              <Lock size={22} weight="duotone" className="text-lc-sky" />
            </div>
            <h2 className="mt-5 font-serif text-2xl text-lc-ink" data-testid="paywall-title">
              {freeUsed ? t("paywall.freeTitle") : t("paywall.title")}
            </h2>
            <p className="mt-2 text-sm text-lc-steel leading-relaxed">
              {freeUsed ? t("paywall.freeSub") : t("paywall.sub")}
            </p>
            <p className="mt-3 flex items-center gap-1.5 text-[11px] font-semibold text-lc-ocean" data-testid="paywall-trust-line">
              <ShieldCheck size={13} weight="fill" /> {t("paywall.trustLine")}
            </p>
            <a href="/guarantee" target="_blank" rel="noreferrer" data-testid="paywall-guarantee-link"
              className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-lc-ok hover:underline">
              <ShieldCheck size={13} weight="duotone" /> {t("guarantee.badge")} — {t("guarantee.readTerms")}
            </a>

            {/* Currency toggle (defaults to HK$ for Hong Kong visitors) */}
            <div className="mt-4 flex items-center gap-2" data-testid="paywall-currency-toggle">
              <span className="text-[11px] font-semibold text-lc-steel">{t("paywall.currency")}</span>
              <div className="flex items-center rounded-sm border border-lc-ink/15 p-0.5">
                {["usd", "hkd"].map((c) => (
                  <button key={c} data-testid={`paywall-ccy-${c}`} onClick={() => changeCurrency(c)}
                    className={`rounded-sm px-2.5 py-1 text-[11px] font-semibold transition-colors duration-200 ${
                      currency === c ? "bg-lc-navy text-white" : "text-lc-steel hover:text-lc-ink"
                    }`}>{c === "usd" ? "USD $" : "HK$"}</button>
                ))}
              </div>
            </div>

            {/* Primary options: Single Audit Pass (low friction) + Professional (recommended), side by side */}
            <div className="mt-3 grid grid-cols-2 gap-2.5" data-testid="paywall-primary">
              {TIERS.filter((x) => x.id === "single" || x.id === "professional").map((tier) => {
                const isRec = tier.id === "professional";
                return (
                  <button
                    key={tier.id}
                    data-testid={`paywall-tier-${tier.id}`}
                    onClick={() => checkout(tier)}
                    disabled={loadingPlan !== null}
                    className={`group relative flex flex-col rounded-sm border p-4 text-left transition-all duration-200 disabled:opacity-60 ${
                      isRec ? "border-lc-ocean bg-lc-ocean/5 ring-1 ring-lc-ocean/30" : "border-lc-ink/15 hover:border-lc-ocean/40 hover:bg-lc-paper"
                    }`}
                  >
                    {isRec && (
                      <span className="absolute -top-2 left-3 inline-flex items-center gap-1 rounded-sm bg-lc-ocean px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white" data-testid="paywall-rec-professional">
                        <Star size={9} weight="fill" /> {t("paywall.recommended")}
                      </span>
                    )}
                    <span className="font-serif text-base text-lc-ink">{tier.name}</span>
                    <p className="mt-0.5 min-h-[28px] text-[11px] leading-snug text-lc-steel">{tier.blurb}</p>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="font-serif text-xl text-lc-ink" data-testid={`paywall-price-${tier.id}`}>{tier[currency].amount}</span>
                      <span className="text-[11px] text-lc-steel">{tier.cadence}</span>
                    </div>
                    <span className={`mt-3 inline-flex items-center justify-center gap-1.5 rounded-sm px-3 py-2 text-xs font-semibold transition-colors duration-200 ${
                      isRec ? "bg-lc-ocean text-white group-hover:bg-lc-navy" : "border border-lc-ink/20 text-lc-ink group-hover:bg-lc-paper"
                    }`}>
                      {loadingPlan === tier.id
                        ? <CircleNotch size={14} className="animate-spin" />
                        : <>{tier.id === "single" ? t("paywall.cta.single") : t("paywall.cta.pro")} <ArrowRight size={13} weight="bold" /></>}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-2.5 text-center text-[11px] font-semibold text-lc-ocean" data-testid="paywall-save-copy">
              {t("paywall.saveCopy")}
            </p>

            {/* Secondary options: Firm + Solo, compact rows */}
            <p className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-lc-steel">{t("paywall.moreOptions")}</p>
            <div className="mt-2 grid gap-2" data-testid="paywall-secondary">
              {TIERS.filter((x) => x.id === "firm" || x.id === "solo").map((tier) => (
                <button
                  key={tier.id}
                  data-testid={`paywall-tier-${tier.id}`}
                  onClick={() => checkout(tier)}
                  disabled={loadingPlan !== null}
                  className="group flex items-center justify-between gap-3 rounded-sm border border-lc-ink/15 px-4 py-2.5 text-left transition-all duration-200 hover:border-lc-ocean/40 hover:bg-lc-paper disabled:opacity-60"
                >
                  <div className="min-w-0">
                    <span className="font-serif text-sm text-lc-ink">{tier.name}</span>
                    <p className="mt-0.5 text-[11px] text-lc-steel">{tier.blurb}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <div className="text-right">
                      <span className="font-serif text-base text-lc-ink" data-testid={`paywall-price-${tier.id}`}>{tier[currency].amount}</span>
                      <span className="text-[11px] text-lc-steel">{tier.cadence}</span>
                    </div>
                    {loadingPlan === tier.id
                      ? <CircleNotch size={15} className="animate-spin text-lc-ocean" />
                      : <ArrowRight size={14} weight="bold" className="text-lc-steel group-hover:text-lc-ocean" />}
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-2.5">
              <button data-testid="paywall-view-plans" onClick={() => { onClose?.(); nav("/"); setTimeout(() => { const el = document.getElementById("pricing"); if (el) el.scrollIntoView({ behavior: "smooth" }); else window.location.hash = "#pricing"; }, 350); }}
                className="inline-flex items-center justify-center gap-2 rounded-sm border border-lc-ink/15 px-5 py-2.5 text-xs font-semibold text-lc-ink hover:bg-lc-paper transition-colors duration-200">
                {t("paywall.viewPlans")} <ArrowRight size={14} weight="bold" />
              </button>
              <button data-testid="paywall-use-byo" onClick={() => { onSwitchByo?.(); onClose?.(); }}
                className="inline-flex items-center justify-center gap-2 rounded-sm border border-lc-ink/15 px-5 py-2.5 text-xs font-semibold text-lc-ink hover:bg-lc-paper transition-colors duration-200">
                <Key size={14} weight="duotone" /> {t("paywall.byo")}
              </button>
            </div>

            <AgreeNote prefix="By completing payment, you accept our" variant="light" className="mt-3 text-center" testid="paywall-agree-note" />

            <div className="mt-6 border-t border-lc-ink/10 pt-5">
              <p className="text-xs font-semibold text-lc-steel">{t("paywall.already")}</p>
              <div className="mt-2 flex gap-2">
                <div className="relative flex-1">
                  <EnvelopeSimple size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-lc-steel" />
                  <input
                    data-testid="paywall-email-input" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder={t("paywall.emailPh")}
                    className="w-full rounded-sm border border-lc-ink/15 bg-lc-paper pl-8 pr-3 py-2 text-xs outline-none focus:border-lc-ocean focus:ring-2 focus:ring-lc-ocean/20 transition-colors duration-200"
                  />
                </div>
                <button data-testid="paywall-unlock-btn" onClick={unlock} disabled={checking}
                  className="rounded-sm bg-lc-ocean px-4 py-2 text-xs font-semibold text-white hover:bg-lc-navy transition-colors duration-200 disabled:opacity-60">
                  {checking ? "…" : t("paywall.unlock")}
                </button>
              </div>

              <p className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-lc-steel">
                <UsersThree size={14} weight="duotone" className="text-lc-ocean" /> {t("paywall.invite")}
              </p>
              <div className="mt-2 flex gap-2">
                <input
                  data-testid="paywall-invite-input" value={code}
                  onChange={(e) => setCode(e.target.value)} placeholder={t("paywall.invitePh")}
                  className="w-full flex-1 rounded-sm border border-lc-ink/15 bg-lc-paper px-3 py-2 font-mono text-xs uppercase outline-none focus:border-lc-ocean focus:ring-2 focus:ring-lc-ocean/20 transition-colors duration-200"
                />
                <button data-testid="paywall-join-btn" onClick={joinTeam} disabled={joining}
                  className="rounded-sm bg-lc-navy px-4 py-2 text-xs font-semibold text-white hover:bg-lc-ocean transition-colors duration-200 disabled:opacity-60">
                  {joining ? "…" : t("paywall.join")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

